import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaRobot, FaPaperPlane, FaSpinner, FaSave, FaBuilding, FaBoxes, FaShieldAlt, FaPercent } from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ContainerQuoteWizard from '../components/ContainerQuoteWizard';
import { rateCards, quotes, clients, users } from '../api';

function ContainerQuotePage({ onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [user, setUser] = useState({});
  const [clientsList, setClientsList] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const loadedMarginRef = useRef(null);
  const [marginPct, setMarginPct] = useState('5');
  const [terms, setTerms] = useState([]);
  const [customTerms, setCustomTerms] = useState('');

  const [customer, setCustomer] = useState({
    customer_id: '',
    customer_name: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    salesperson_id: '',
    payment_terms: '30 Days',
    vat_included: false,
    expiry_days: 30,
    comments: ''
  });

  const [chatMessages, setChatMessages] = useState([
    { role: 'ai', text: "Hi! Tell me what containers you need moved - e.g. \"2 x 6m containers from Benoni to Durban, they are empty\". I'll fill the form for you. I never set the price - the rate engine does that." }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [calcQuote, setCalcQuote] = useState(null);
  const [liveWizard, setLiveWizard] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch { /* ignore */ }
    clients.getAll().then(res => {
      if (res.success) setClientsList(res.clients || []);
    }).catch(() => { /* non-critical */ });
    users.getAll().then(res => {
      if (res.success) {
        setSalespersons(res.users?.filter(u => u.role === 'admin' || u.role === 'super_admin') || []);
      }
    }).catch(() => { /* non-critical */ });

    // ── Pre-fill from CRM: if ?company=X&contact=Y&email=Z in URL ──
    const params = new URLSearchParams(window.location.search);
    const crmCompany = params.get('company');
    if (crmCompany) {
      setCustomer(prev => ({
        ...prev,
        customer_id: '',  // triggers "Other Customer" mode
        customer_name: crmCompany,
        contact_person: params.get('contact') || '',
        contact_email: params.get('email') || ''
      }));
    }

    if (isEdit && id) {
      setLoadingQuote(true);
      quotes.getOne(id).then(res => {
        if (res.success && res.quote) {
          const q = res.quote;
          setCustomer(prev => ({
            ...prev,
            customer_id: q.customer_id || '',
            customer_name: q.customer_name || '',
            contact_person: q.contact_person || '',
            contact_phone: q.contact_phone || '',
            contact_email: q.contact_email || '',
            salesperson_id: q.salesperson_id || '',
            payment_terms: q.payment_terms || '30 Days',
            vat_included: q.vat_included || false,
            expiry_days: q.expiry_days || 30,
            comments: q.comments || ''
          }));
          const savedMargin = parseFloat(q.margin_percentage);
          if (!isNaN(savedMargin) && savedMargin > 0) {
            loadedMarginRef.current = savedMargin;
            setMarginPct((savedMargin * 100).toFixed(1));
          } else {
            loadedMarginRef.current = 0.05;
            setMarginPct('5');
          }
          if (q.terms_conditions) {
            const savedTerms = String(q.terms_conditions).split(',').filter(Boolean);
            if (savedTerms.length > 0) setTerms(savedTerms);
          }
          if (q.custom_terms) setCustomTerms(q.custom_terms);
          if (q.rate_card_details) {
            window.dispatchEvent(new CustomEvent('container-wizard-fill', { detail: q.rate_card_details }));
          }
        } else {
          toast.error(res.error || 'Failed to load quote');
        }
      }).catch(err => {
        console.error('Load container quote error:', err);
        toast.error(err.message || 'Failed to load quote');
      }).finally(() => setLoadingQuote(false));
    }
  }, [isEdit, id]);

  const handleCustomerChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomer(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCustomerSelect = (e) => {
    const sel = e.target.value;
    const found = clientsList.find(c => c.id === sel);
    setCustomer(prev => ({
      ...prev,
      customer_id: sel,
      customer_name: found ? found.company_name : prev.customer_name,
      contact_person: found ? (found.contact_person || '') : prev.contact_person,
      contact_phone: found ? (found.contact_phone || '') : prev.contact_phone,
      contact_email: found ? (found.contact_email || '') : prev.contact_email
    }));
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;

    setChatMessages(prev => [...prev, { role: 'user', text }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const history = chatMessages.slice(-10);
      const res = await rateCards.assist(text, liveWizard, history);
      if (res.success) {
        const aiReplies = [];
        if (res.reply) aiReplies.push({ role: 'ai', text: res.reply });
        if (res.summary && !res.reply) aiReplies.push({ role: 'ai', text: `Here's what I've got so far: ${res.summary}` });
        if (res.questions && res.questions.length > 0) {
          aiReplies.push({ role: 'ai', text: res.questions.join(' ') });
        }
        if (res.fields && Object.keys(res.fields).length > 0) {
          window.dispatchEvent(new CustomEvent('container-wizard-fill', { detail: res.fields }));
        }
        setChatMessages(prev => [...prev, ...aiReplies]);
      } else {
        setChatMessages(prev => [...prev, { role: 'ai', text: `Error: ${res.error || 'Assistant error'}` }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'ai', text: `Error: ${err.message || 'Assistant unavailable. Manual wizard still works.'}` }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Rebuild totals for a NEW margin % (base cost fixed; margin + client price re-derive)
  const applyMargin = (q, pct) => {
    if (!q) return q;
    const m = parseFloat(pct);
    const margin = isNaN(m) || m < 0 ? 0 : m / 100;
    const base = parseFloat(q.totals?.base_cost) || 0;
    const marginAmount = Math.round(base * margin * 100) / 100;
    const quoteTotal = Math.round((base + marginAmount) * 100) / 100;
    const vatIncl = q.totals?.vat_included === true || q.totals?.vat_included === 'true';
    const vatAmount = vatIncl ? Math.round(quoteTotal * 0.15 * 100) / 100 : 0;
    const grandTotal = Math.round((quoteTotal + vatAmount) * 100) / 100;
    return {
      ...q,
      margin_percentage: margin,
      totals: { ...q.totals, margin_amount: marginAmount, quote_total: quoteTotal, vat_amount: vatAmount, grand_total: grandTotal }
    };
  };

  const handleWizardCalculated = (q) => {
    const override = loadedMarginRef.current || parseFloat(q.margin_percentage) || 0.05;
    setCalcQuote(applyMargin(q, override * 100));
  };

  const handleWizardClear = () => {
    setCalcQuote(null);
    loadedMarginRef.current = null;
    setMarginPct('5');
  };

  const handleMarginChange = (e) => {
    const val = e.target.value;
    setMarginPct(val);
    setCalcQuote(prev => applyMargin(prev, val));
  };

  const toggleTerm = (term) => {
    setTerms(prev => prev.includes(term) ? prev.filter(t => t !== term) : [...prev, term]);
  };

  // Items: cost_price = rate-card applied cost per unit; unit_price = cost x (1+margin).
  // Margin is baked in - never shown to the client. Qty = real truck count.
  const buildQuoteItems = (calcQuote) => {
    if (!calcQuote) return [];
    const marginPct = parseFloat(calcQuote.margin_percentage) || 0;
    const multiplier = 1 + marginPct;
    const engineTotal = parseFloat(calcQuote.totals?.quote_total) || 0;
    const items = (calcQuote.line_items || []).map((li) => {
      const qty = typeof li.quantity === 'number' && li.quantity ? li.quantity : 1;
      let costPerUnit;
      if (li.type === 'base' && typeof li.fuel_indexed_unit_price === 'number') {
        costPerUnit = parseFloat(li.fuel_indexed_unit_price);
      } else {
        const lineTotal = (typeof li.total === 'number' && li.total != null)
          ? parseFloat(li.total)
          : (parseFloat(li.unit_price) || 0) * qty;
        costPerUnit = lineTotal / qty;
      }
      const roundedCost = Math.round(costPerUnit * 100) / 100;
      const unitPrice = Math.round(roundedCost * multiplier * 100) / 100;
      return {
        description: li.description,
        quantity: qty,
        cost_price: roundedCost,
        unit_price: unitPrice,
        total: Math.round(qty * unitPrice * 100) / 100,
        margin_percentage: Math.round(marginPct * 100)
      };
    });
    // Exact-cent correction so saved items sum EXACTLY to the wizard's quote total
    const sumItems = items.reduce((s, it) => s + (it.total || 0), 0);
    const diff = Math.round((engineTotal - sumItems) * 100) / 100;
    if (items.length && Math.abs(diff) > 0.001) {
      items[0].total = Math.round((items[0].total + diff) * 100) / 100;
      items[0].unit_price = Math.round((items[0].total / items[0].quantity) * 100) / 100;
    }
    return items;
  };

  // Human-readable description, e.g.
  // "2 x 12m dry containers transported from Soweto to Waterkloof. Includes loading and offloading. No extras."
  const buildDescription = (q) => {
    if (!q) return null;
    const req = q.request || {};
    const count = req.container_count || 1;
    const size = req.container_size || '6m';
    const type = String(req.container_type || 'dry').replace(/_/g, ' ');
    const from = req.pickup_town ? String(req.pickup_town).split(',')[0].trim() : '';
    const to = req.dropoff_town ? String(req.dropoff_town).split(',')[0].trim() : '';
    const load = req.site_loading ? 'Client loads and offloads' : 'Includes loading and offloading';
    const extras = [];
    if (req.weekend) extras.push('weekend/overtime');
    if (req.cross_border) extras.push('cross-border');
    if (req.mine_site) extras.push('mine safety');
    if (req.extra_hours) extras.push('extra hours');
    if (req.standing_hours) extras.push('standing time');
    if (req.extra_stops) extras.push('extra stops');
    const extrasTxt = extras.length ? ` Extras: ${extras.join(', ')}.` : ' No extras.';
    return `${count} x ${size} ${type} container${count > 1 ? 's' : ''} transported from ${from} to ${to}. ${load}.${extrasTxt}`;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!customer.customer_name && !customer.customer_id) {
      toast.error('Enter a customer name or select a client');
      return;
    }
    if (!calcQuote) {
      toast.error('Calculate the quote first (bottom of the wizard)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        quote_type: 'container',
        customer_id: customer.customer_id || null,
        customer_name: customer.customer_name || null,
        contact_person: customer.contact_person || null,
        contact_phone: customer.contact_phone || null,
        contact_email: customer.contact_email || null,
        salesperson_id: customer.salesperson_id || null,
        payment_terms: customer.payment_terms,
        vat_included: customer.vat_included,
        expiry_days: customer.expiry_days,
        comments: customer.comments || null,
        terms_conditions: terms.length ? terms.join(',') : 'standard',
        custom_terms: terms.includes('custom') ? (customTerms || null) : null,
        description: buildDescription(calcQuote),
        origin: calcQuote?.request?.pickup_town || null,
        destination: calcQuote?.request?.dropoff_town || null,
        distance_km: calcQuote?.town?.km_from_edenvale || null,
        truck_type: calcQuote?.truck?.code || null,
        rate_card_details: calcQuote?.request || null,
        rate_card_breakdown: calcQuote?.line_items || null,
        fuel_index_at_quote: calcQuote?.fuel?.index || null,
        rate_card_version: 'benecke-2021-fuel-indexed',
        margin_percentage: calcQuote?.margin_percentage != null ? calcQuote.margin_percentage : null,
        margin_amount: calcQuote?.totals?.margin_amount != null ? calcQuote.totals.margin_amount : null,
        items: buildQuoteItems(calcQuote),
        route: {
          pickup_address: calcQuote?.request?.pickup_town || '',
          delivery_address: calcQuote?.request?.dropoff_town || '',
          distance_km: calcQuote?.town?.km_from_edenvale || null
        },
        created_by: user.id || null
      };

      const res = isEdit && id ? await quotes.update(id, payload) : await quotes.create(payload);
      if (res.success) {
        toast.success(isEdit ? 'Container quote updated!' : `Container quote ${res.quote?.quote_number || ''} created!`);
        navigate('/quotes');
      } else {
        toast.error(res.error || 'Failed to save quote');
      }
    } catch (err) {
      console.error('Save container quote error:', err);
      toast.error(err.message || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";
  const TERM_OPTIONS = [
    ['standard', 'One Purpose Standard Terms'],
    ['enm', 'E&M Terms'],
    ['lovemore', 'Lovemore Terms'],
    ['transcor', 'Transcor Terms'],
    ['benecke', 'Benecke Transport Terms'],
    ['custom', 'Custom Terms']
  ];

  const marginAmt = parseFloat(calcQuote?.totals?.margin_amount) || 0;
  const clientPrice = parseFloat(calcQuote?.totals?.grand_total) || 0;
  const costTotal = parseFloat(calcQuote?.totals?.base_cost) || 0;

  return (
    <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/quotes" className="p-2 rounded-lg hover:bg-gray-100 text-navy">
            <FaArrowLeft />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-navy flex items-center gap-2">
              <FaBoxes className="text-yellow-500" /> {isEdit ? 'Edit Container Quote' : 'Container Quote'}
            </h1>
            <p className="text-sm text-gray-500">
              {isEdit ? 'Update the quote details. The rate card pre-fills the cost - adjust the margin below as needed.' : 'Chat to the AI assistant or use the manual wizard.'}
            </p>
          </div>
        </div>

        {loadingQuote ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin text-4xl text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-500">Loading container quote...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <FaBuilding className="text-yellow-500" /> Customer Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls}>Select Client (or type new below)</label>
                  <select className={inputCls} value={customer.customer_id} onChange={handleCustomerSelect}>
                    <option value="">- New / Walk-in customer -</option>
                    {clientsList.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Customer Name *</label>
                  <input name="customer_name" value={customer.customer_name} onChange={handleCustomerChange} className={inputCls} placeholder="e.g. Benecke" required />
                </div>
                <div>
                  <label className={labelCls}>Contact Person</label>
                  <input name="contact_person" value={customer.contact_person} onChange={handleCustomerChange} className={inputCls} placeholder="Full name" />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input name="contact_phone" value={customer.contact_phone} onChange={handleCustomerChange} className={inputCls} placeholder="+27 ..." />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input name="contact_email" value={customer.contact_email} onChange={handleCustomerChange} className={inputCls} placeholder="name@email.com" />
                </div>
                <div>
                  <label className={labelCls}>Sales Consultant</label>
                  <select name="salesperson_id" value={customer.salesperson_id} onChange={handleCustomerChange} className={inputCls}>
                    <option value="">- Select consultant -</option>
                    {salespersons.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Payment Terms</label>
                  <select name="payment_terms" value={customer.payment_terms} onChange={handleCustomerChange} className={inputCls}>
                    <option>Pay in Advance</option>
                    <option>7 Days</option>
                    <option>14 Days</option>
                    <option>30 Days</option>
                    <option>45 Days</option>
                    <option>60 Days</option>
                    <option>90 Days</option>
                    <option>On Delivery</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Expiry (days)</label>
                  <select name="expiry_days" value={customer.expiry_days} onChange={handleCustomerChange} className={inputCls}>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={45}>45 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input name="vat_included" type="checkbox" checked={customer.vat_included} onChange={handleCustomerChange} className="rounded" />
                    Prices include VAT (15%)
                  </label>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Comments</label>
                  <textarea name="comments" value={customer.comments} onChange={handleCustomerChange} rows="2" className={inputCls} placeholder="Special instructions, access notes..." />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 flex flex-col">
                <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <FaRobot className="text-yellow-500" /> AI Assistant
                </h2>
                <div className="flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1 mb-4 bg-gray-50 rounded-lg p-3">
                  {chatMessages.map((m, i) => (
                    <div key={i} className={`max-w-[90%] px-3 py-2 rounded-lg text-sm whitespace-pre-line ${m.role === 'user' ? 'bg-yellow-400 text-navy ml-auto font-medium' : 'bg-white border border-gray-200 text-gray-800'}`}>
                      {m.text}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="bg-white border border-gray-200 px-3 py-2 rounded-lg text-sm text-gray-500 flex items-center gap-2 max-w-[90%]">
                      <FaSpinner className="animate-spin text-yellow-500" /> Thinking...
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChat(); } }}
                    placeholder="e.g. 2 containers from Benoni to Durban, empty"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={sendChat}
                    disabled={chatLoading}
                    className="px-4 py-2 bg-yellow-400 text-navy rounded-lg hover:bg-yellow-300 disabled:opacity-50 flex items-center gap-1 text-sm font-bold shadow"
                  >
                    <FaPaperPlane size={12} />
                  </button>
                </div>
              </div>

              <div className="lg:col-span-3">
                <ContainerQuoteWizard onCalculated={handleWizardCalculated} onClear={handleWizardClear} onStateChange={(w) => setLiveWizard(w)} />

                {calcQuote && (
                  <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4 text-sm">
                    <div className="flex flex-wrap items-end gap-4">
                      <div>
                        <label className={labelCls}><FaPercent className="inline text-yellow-500 mr-1" />Margin % (editable)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={marginPct}
                          onChange={handleMarginChange}
                          className="w-24 px-3 py-2 border border-yellow-300 rounded-lg text-sm font-bold text-navy bg-yellow-50 focus:outline-none focus:border-yellow-500"
                          placeholder="5"
                        />
                      </div>
                      <div className="space-y-1">
                        <div>Rate Card Cost: <strong className="text-navy">R {costTotal.toFixed(2)}</strong></div>
                        <div>Margin Amount: <strong className="text-green-700">R {marginAmt.toFixed(2)}</strong></div>
                        <div>Client Price: <strong className="text-green-700">R {clientPrice.toFixed(2)}</strong></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                <FaShieldAlt className="text-green-500" /> Terms & Conditions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {TERM_OPTIONS.map(([val, lbl]) => (
                  <label key={val} className="flex items-center gap-2 text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={terms.includes(val)} onChange={() => toggleTerm(val)} className="rounded" />
                    {lbl}
                  </label>
                ))}
              </div>
              {terms.includes('custom') && (
                <textarea
                  value={customTerms}
                  onChange={(e) => setCustomTerms(e.target.value)}
                  rows="3"
                  className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Enter your custom terms and conditions..."
                />
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => navigate('/quotes')} className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-yellow-400 hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2">
                {saving ? <><FaSpinner className="animate-spin" /> Saving...</> : <><FaSave /> {isEdit ? 'Update Container Quote' : 'Save Container Quote'}</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </AdminLayout>
  );
}

export default ContainerQuotePage;